import { useEffect, useRef, useState } from "react";
import { Modal, ModalDialog } from "@mui/joy";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import IconButton from "@mui/joy/IconButton";
import CircularProgress from "@mui/joy/CircularProgress";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import BottomSheet from "../BottomSheet/BottomSheet";
import { bookApi } from "../../api.js";
import { confirmCoverLayoutModal } from "../confirmCoverLayoutModal.jsx";
import { coverPhotoGeometry, coverContentLayout, normalizeCoverShape } from "../../coverPhotoGeometry.js";
import PdfCoverText from "../PdfCoverText.jsx";
import StickerPlaceholderIcon from "../StickerPlaceholderIcon.jsx";

function PhotoPlaceholder({ style }) {
  return <div style={{
    background: "#fff", color: "#89818e", overflow: "hidden",
    minWidth: 0, minHeight: 0, containerType: "inline-size",
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", gap: 4, ...style,
  }}>
    <PhotoCameraRoundedIcon sx={{ fontSize: "clamp(12px, 18cqw, 24px)" }} />
    <span style={{ fontSize: "clamp(7px, 11cqw, 12px)", lineHeight: 1.2,
      fontWeight: 400, whiteSpace: "nowrap" }}>Ваше фото</span>
  </div>;
}

export function layoutVariants(layouts, side) {
  const tags = side === "front" ? ["photo", "sticker"] : ["photo", "text", "minimal", "classic"];
  return tags.flatMap(tag => {
    const layout = layouts.find(l => l.side === side && l.tag === tag);
    if (!layout) return [];
    if (tag === "photo") return ["square", "rectangle"].map(shape => ({
      ...layout, shape, key: `${layout.id}:${shape}`,
      title: shape === "square" ? "Фото · квадрат" : "Фото · прямоугольник",
    }));
    return [{ ...layout, key: layout.id,
      title: { text: "Текст и лого", minimal: "Только лого", classic: "4 фотографии", sticker: "Стикер" }[tag] }];
  });
}

export function LayoutPreview({ side, tag, shape, bookSize, bgColor,
  previewTitle, previewYear, dateMode, titleColor, yearColor, stickerImage }) {
  const solo = tag === "photo";
  const kind = solo ? shape : side === "front" ? "sticker" : "grid";
  const layout = coverContentLayout(bookSize, kind);
  const photo = solo ? coverPhotoGeometry(bookSize, shape) : null;
  return <div style={{ position: "relative", width: "100%", overflow: "hidden",
    aspectRatio: `${layout.sideW} / ${layout.sideH}`, background: bgColor || "#834A90" }}>
    {tag !== "minimal" && <PdfCoverText layout={layout} title={previewTitle || ""}
      year={previewYear} dateMode={dateMode} showYear={dateMode !== "none"}
      titleColor={titleColor || "#fff"} yearColor={yearColor || "#fff"} />}
    {photo && <PhotoPlaceholder style={photo.sx} />}
    {tag === "sticker" && <div style={{ position: "absolute", ...layout.stickerSx }}>
      {stickerImage ? <img src={stickerImage} alt="" draggable={false}
        style={{ width: "100%", height: "100%", objectFit: "contain",
          objectPosition: layout.sideH === 250 ? "center bottom" : "center", display: "block" }} />
        : <div style={{ width: "100%", height: "100%", boxSizing: "border-box",
          background: "rgba(255,255,255,.12)", border: "1px dashed rgba(255,255,255,.5)",
          display: "grid", placeItems: "center", color: "rgba(255,255,255,.85)" }}><StickerPlaceholderIcon /></div>}
    </div>}
    {tag === "classic" && <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)",
      display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))",
      gridTemplateRows: "repeat(2,minmax(0,1fr))", ...layout.gridSx }}>
      {[0, 1, 2, 3].map(i => <PhotoPlaceholder key={i} />)}
    </div>}
    <img src="/logo.png" alt="" draggable={false} style={layout.logoSx} />
  </div>;
}

export default function CoverLayoutPicker({ open, onClose, side, bookId, currentLayoutId,
  onApplied, bgColor, bookSize, currentPhotoShape, hasFrontPhoto = false, hasBackPhotos = false,
  previewTitle = "", previewYear = String(new Date().getFullYear()), dateMode = "year",
  titleColor = "#fff", yearColor = "#fff", stickerImage = null }) {
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(null);
  const savingRef = useRef(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 700);
  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true); setLayouts([]); setError("");
    bookApi.getCoverLayouts(side).then(data => { if (active) setLayouts(data || []); })
      .catch(() => { if (active) setError("Не удалось загрузить макеты. Закройте окно и попробуйте ещё раз."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, side]);
  const close = () => { if (!savingRef.current) onClose?.(); };
  const variants = layoutVariants(layouts, side);
  const apply = async layout => {
    if (savingRef.current) return;
    const changed = layout.id !== currentLayoutId ||
      (layout.shape !== undefined && layout.shape !== normalizeCoverShape(currentPhotoShape));
    if (!changed) { close(); return; }
    // Блокируем и повторный клик во время подтверждения сброса фото.
    savingRef.current = true;
    setSaving(layout.key);
    try {
      const hasPhotos = side === "front" ? hasFrontPhoto : hasBackPhotos;
      if (hasPhotos && !await confirmCoverLayoutModal()) return;
      const patch = side === "front"
        ? { frontLayoutId: layout.id, frontPhotoShape: layout.shape }
        : { backLayoutId: layout.id, backPhotoShape: layout.shape };
      const updated = await bookApi.setBookCoverLayouts(bookId, patch);
      onApplied?.(updated);
      onClose?.();
    } catch (e) {
      setError("Не удалось применить макет. Попробуйте ещё раз.");
    } finally { savingRef.current = false; setSaving(null); }
  };
  const title = `Макет ${side === "front" ? "передней" : "задней"} обложки`;
  const body = <>
    {error && <div role="alert" style={{ color: "#b42318", marginBottom: 12 }}>{error}</div>}
    {loading ? <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}><CircularProgress size="sm" /></Box>
      : !variants.length ? <Typography>Макеты не найдены.</Typography>
      : <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 12 }}>
        {variants.map(l => {
          const active = l.id === currentLayoutId && (l.shape === undefined || l.shape === normalizeCoverShape(currentPhotoShape));
          const lastSticker = side === "front" && l.tag === "sticker";
          return <button type="button" key={l.key} onClick={() => apply(l)} disabled={!!saving}
            aria-pressed={active}
            style={{ boxSizing: "border-box", minWidth: 0, display: "flex", flexDirection: "column", alignItems: "stretch",
              gridColumn: lastSticker ? "1 / -1" : "auto",
              width: lastSticker ? "calc((100% - 12px) / 2)" : "100%", justifySelf: "center",
              border: `2px solid ${active ? "#834A90" : "#e6e6e6"}`,
              borderRadius: 12, background: "#fff", padding: 8, color: "#202020",
              fontFamily: "inherit", cursor: "pointer", opacity: saving && saving !== l.key ? .5 : 1 }}>
            <LayoutPreview {...{ side, bookSize, bgColor, previewTitle, previewYear,
              dateMode, titleColor, yearColor, stickerImage }} tag={l.tag} shape={l.shape} />
            <div style={{ marginTop: 8, minHeight: 34, lineHeight: 1.3, fontSize: 13, fontWeight: 600 }}>{l.title}</div>
            {saving === l.key && <div style={{ fontSize: 11 }}>Применяем…</div>}
          </button>;
        })}
      </div>}
  </>;
  if (isMobile) return <BottomSheet open={open} onClose={close} title={title}>
    <div style={{ padding: "4px 16px 20px" }}>{body}</div>
  </BottomSheet>;
  return <Modal open={open} onClose={close}>
    <ModalDialog sx={{ maxWidth: 540, width: "calc(100% - 32px)", maxHeight: "85dvh", overflowY: "auto", borderRadius: 18 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography level="title-lg">{title}</Typography>
        <IconButton size="sm" onClick={close} disabled={!!saving} sx={{ ml: "auto" }} aria-label="Закрыть"><CloseRoundedIcon /></IconButton>
      </Box>
      {body}
    </ModalDialog>
  </Modal>;
}